import { useState, useMemo, useEffect } from "react";
import { API_ENDPOINTS, API_URL } from "../../../constants/constants";

// Interface pour les données reçues de l'API
interface TeacherApiData {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  fonction: string;
  service: string;
  profileImage: string | null;
  citation: string | null;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
}

// Interface pour notre modèle de données interne
interface Professeur {
  id: number;
  nom: string;
  profileImage: string;
  citation: string;
  matieres: string;
}

const ProfesseursExperts = () => {
  const [filterType, setFilterType] = useState<"nom" | "matieres">("nom");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProfesseur, setSelectedProfesseur] = useState<number | null>(
    null
  );
  const [messageText, setMessageText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [professeurs, setProfesseurs] = useState<Professeur[]>([]);
  const professorsPerPage = 6; // Nombre de professeurs par page

  // Chargement des professeurs depuis l'API
  useEffect(() => {
    const fetchProfesseurs = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const accessToken = localStorage.getItem("accessToken");

        if (!accessToken) {
          throw new Error("Vous n'êtes pas connecté");
        }

        const response = await fetch(API_ENDPOINTS.AUTH.GET_TEACHERS, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Erreur ${response.status}: ${response.statusText}`);
        }

        const data = (await response.json()) as TeacherApiData[];

        // Transformation des données de l'API pour correspondre à notre structure
        const transformedData = data.map((teacher: TeacherApiData) => ({
          id: teacher.id,
          nom: `${teacher.firstName} ${teacher.lastName}`,
          profileImage: teacher.profileImage
            ? `${API_URL}/${teacher.profileImage}`
            : `${API_URL}/images/default-profile.jpg`,
          citation: teacher.citation || "Informations non disponibles",
          matieres: teacher.fonction || "Non spécifié",
        }));

        setProfesseurs(transformedData);
      } catch (err) {
        console.error("Erreur lors du chargement des professeurs:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Impossible de charger la liste des professeurs"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfesseurs();
  }, []);

  // Tri et filtrage des professeurs
  const professeursFiltres = useMemo(() => {
    let professeursCopie = [...professeurs];

    // Appliquer la recherche si une requête est présente
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      professeursCopie = professeursCopie.filter(
        (prof) =>
          prof.nom.toLowerCase().includes(query) ||
          prof.matieres.toLowerCase().includes(query)
      );
    }

    // Trier les résultats
    if (filterType === "nom") {
      return professeursCopie.sort((a, b) => a.nom.localeCompare(b.nom));
    } else if (filterType === "matieres") {
      return professeursCopie.sort((a, b) =>
        a.matieres.localeCompare(b.matieres)
      );
    }

    return professeursCopie;
  }, [professeurs, filterType, searchQuery]);

  // Calculer les professeurs à afficher sur la page actuelle
  const indexOfLastProfessor = currentPage * professorsPerPage;
  const indexOfFirstProfessor = indexOfLastProfessor - professorsPerPage;
  const currentProfessors = professeursFiltres.slice(
    indexOfFirstProfessor,
    indexOfLastProfessor
  );
  const totalPages = Math.ceil(professeursFiltres.length / professorsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleSendMessage = () => {
    // Ici, vous pourriez ajouter la logique pour envoyer le message
    alert(
      `Message envoyé à ${
        professeurs.find((p) => p.id === selectedProfesseur)?.nom
      }`
    );
    setMessageText("");
    setSelectedProfesseur(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Réinitialiser à la première page lors de la recherche
  };

  return (
    <div className="bg-[#fefaf6] py-2 min-h-screen w-full">
      <div className="container mx-auto px-4 relative">
        <h1 className="text-2xl font-bold mb-8 text-gray-800">
          Nos Professeurs et Experts
        </h1>

        {/* Barre de recherche et filtre */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8">
          {/* Barre de recherche */}
          <div className="w-full md:w-auto mb-4 md:mb-0 mr-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher par nom ou matière..."
                className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  ></path>
                </svg>
              </div>
            </div>
          </div>

          {/* Filtre */}
          <div className="relative">
            <span className="text-gray-700 mr-2">Trier par</span>
            <div className="inline-block border rounded-sm">
              <select
                className="bg-white py-2 pl-3 pr-8 appearance-none"
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value as "nom" | "matieres");
                  setCurrentPage(1); // Réinitialiser à la première page lors du changement de filtre
                }}
              >
                <option value="nom">Nom</option>
                <option value="matieres">Matières enseignées</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg
                  className="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Message de chargement */}
        {isLoading && (
          <div className="text-center py-10">
            <p className="text-gray-600">Chargement des professeurs...</p>
          </div>
        )}

        {/* Message d'erreur */}
        {error && !isLoading && (
          <div className="text-center py-10">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Message quand aucun résultat */}
        {!isLoading && !error && professeursFiltres.length === 0 && (
          <div className="text-center py-10">
            <p className="text-gray-600">
              Aucun professeur ne correspond à votre recherche.
            </p>
          </div>
        )}

        {/* Grille des professeurs */}
        {!isLoading && !error && professeursFiltres.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            {currentProfessors.map((professeur) => (
              <div
                key={professeur.id}
                className="bg-white rounded-md overflow-hidden shadow-sm border border-gray-100"
              >
                <div className="flex mobile:flex-col tablet:flex-col relative">
                  {/* Image du professeur */}
                  <div className="w-1/4 mobile:w-full tablet:w-full relative">
                    <img
                      src={professeur.profileImage}
                      alt={professeur.nom}
                      className="h-40 w-40 object-cover object-center"
                    />

                    {/* Nom pour mobile et tablette - positionné à droite et au milieu dans la div de l'image */}
                    <div className="hidden mobile:block tablet:block absolute top-1/2 right-0 transform -translate-y-1/2">
                      <div className="bg-orange-500 text-white text-xs font-semibold px-4 py-1 rounded-l-md shadow-md">
                        {professeur.nom}
                        <div className="text-[10px] text-white/80">
                          {professeur.matieres}
                        </div>
                      </div>
                    </div>

                    {/* Bouton pour écrans moyens et grands */}
                    <div className="mt-4 mobile:hidden tablet:hidden md:flex justify-center pb-2 items-center">
                      <button
                        className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-4 py-2 rounded-sm"
                        onClick={() => setSelectedProfesseur(professeur.id)}
                      >
                        Lui écrire
                      </button>
                    </div>
                  </div>

                  {/* Nom du professeur à cheval sur écrans moyens et grands uniquement */}
                  <div className="absolute top-0 md:left-[calc(25%-60px)] md:block mobile:hidden tablet:hidden transform md:-translate-x-0 z-20">
                    <div className="bg-orange-500 text-white text-xs font-semibold px-4 py-1 rounded-md shadow-md">
                      {professeur.nom}
                      <div className="text-[10px] text-white/80">
                        {professeur.matieres}
                      </div>
                    </div>
                  </div>

                  {/* Contenu textuel */}
                  <div className="w-3/4 mobile:w-full tablet:w-full p-6 relative">
                    {/* Contenu de la citation */}
                    <div className="relative md:pt-5">
                      <p className="text-gray-700 text-sm mb-4 relative z-10">
                        {professeur.citation}
                      </p>
                    </div>

                    {/* Bouton pour mobile et tablette - positionné sous la citation */}
                    <div className="hidden mobile:flex tablet:flex justify-center mt-2 items-center">
                      <button
                        className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-4 py-2 rounded-sm"
                        onClick={() => setSelectedProfesseur(professeur.id)}
                      >
                        Lui écrire
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !error && professeursFiltres.length > 0 && (
          <div className="flex justify-center border-t border-gray-200 pt-4 pb-8">
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`flex items-center text-sm px-2 py-1 rounded-sm ${
                  currentPage === 1
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                &lt;&lt; Page précédente
              </button>

              <span className="text-sm text-gray-500">
                Page {currentPage} sur {totalPages}
              </span>

              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`flex items-center justify-center space-x-1 bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-2 rounded-sm ${
                  currentPage === totalPages
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                <span>Page suivante</span>
                <span className="ml-1">&gt;&gt;</span>
              </button>
            </div>
          </div>
        )}

        {/* Boîte de dialogue pour envoyer un message */}
        {selectedProfesseur !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 mx-3">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">
                Envoyer un message à{" "}
                {professeurs.find((p) => p.id === selectedProfesseur)?.nom}
              </h2>
              <textarea
                className="w-full border rounded p-2 mb-4 h-32"
                placeholder="Écrivez votre message ici..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
              />
              <div className="flex justify-end space-x-2">
                <button
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
                  onClick={() => setSelectedProfesseur(null)}
                >
                  Annuler
                </button>
                <button
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                  onClick={handleSendMessage}
                  disabled={!messageText.trim()}
                >
                  Envoyer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfesseursExperts;
